import { AnyAction } from 'redux'
import {
  startLoading,
  stopLoading,
} from '../../../common/modules/loading/actions'
import { LoadingTypes } from '../../../common/modules/loading/reducer'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import { RequestFailureMessage } from '../../../common/utils/messagesContants'
import { AxiosError, AxiosResponse } from 'axios'
import { camelize } from 'casing'
import { getReportsV2, getSearchedData } from '../../api/markeplace'
import { getAzureRevenueReports } from '../../api/azure'
import { getGcpRevenueReports } from '../../api/gcp'
import { CloudMarketplace } from '../productsListing/reducer'
import { getErrorMessages } from '../../../common/utils/error'
import {
  DateRange,
  FiltersState,
  LastEvaluatedObject,
  ReportsPayload,
} from './reducer'
import { AppDispatch, RootState } from '../../../store'
import { isEmpty } from 'lodash'
import { errorLogger } from '../../../common/utils/errorLogger'
import FileDownload from 'js-file-download'
import { convertObjectKeysAndValuesToCommaSeprated } from '../../../common/utils/helperFunctions'

export enum RevenueTrackingActionTypes {
  SET_REPORTS_DATA = 'SET_REPORTS_DATA',
  REVENUE_SET_PAGE_NUMBER = 'REVENUE_SET_PAGE_NUMBER',
  REVENUE_SET_LIMIT = 'REVENUE_SET_LIMIT',
  SET_PRODUCT_ID = 'SET_PRODUCT_ID',
  CLEAR_REVENUE_REPORTS = 'CLEAR_REVENUE_REPORTS',
  SET_DATE_RANGE = 'SET_DATE_RANGE',
  SET_DATE_RANGE_TYPE = 'SET_DATE_RANGE_TYPE',
  SET_FILTERS = 'SET_FILTERS',
  CLEAR_REVENUE_FILTERS = 'CLEAR_REVENUE_FILTERS',
}
export type GetRevenueInsightsReportV2 = {
  partnerId: string
  finalStartDate: string
  finalEndDate: string
  downloadReport?: boolean
  pageSize: number
  pageNumber?: number
  productId?: string
  dateRangeType?: string
  paymentStatus?: string
  offerType?: string
  disbursementDate?: string | number
  paymentDueDate?: string | number
  paymentDelayedSince?: string | number
  lastEvaluatedKeys?: string
  lastEvaluatedValues?: string
  sortBy?: string
  sortOrder?: string
  shouldSendGetDateParam?: boolean
  input?: string
  selectedInput?: string
  sectionName?: string
}
const handleReportFetch = async ({
  partnerId,
  finalStartDate,
  finalEndDate,
  pageSize,
  productId,
  dateRangeType,
  lastEvaluatedKeys,
  lastEvaluatedValues,
  shouldSendGetDateParam,
  filters,
  input,
  selectedInput,
  pageNumber,
  sectionName,
  cloudMarketplace,
}: {
  partnerId: string
  finalStartDate: string
  finalEndDate: string
  pageSize: number
  productId: string
  dateRangeType?: string
  lastEvaluatedKeys?: string | null
  lastEvaluatedValues?: string | null
  shouldSendGetDateParam: boolean
  filters: any
  input?: string
  selectedInput?: string
  pageNumber?: number
  sectionName?: string
  cloudMarketplace: CloudMarketplace
}) => {
  switch (cloudMarketplace) {
    case 'AWS': {
      if (isEmpty(input)) {
        const { data } = await getReportsV2({
          partnerId,
          finalStartDate,
          finalEndDate,
          pageSize,
          productId,
          dateRangeType,
          lastEvaluatedKeys,
          lastEvaluatedValues,
          shouldSendGetDateParam,
          ...filters,
        })
        return data
      } else {
        const response = await getSearchedData({
          partnerId,
          productId,
          input,
          selectedInput,
          pageSize,
          pageNumber,
          sectionName,
          isRevenueSearch: true,
        })
        const numberOfPage = response?.data?.pagination_object?.current_page
        const totalCount = response?.data.total_count
        const rows = response?.data.records.map((recordItem: any) => ({
          ...recordItem._source,
        }))
        const dataSearched = {
          ...response.data,
          records: rows,
          page_size: pageSize,
          page_number: numberOfPage || 1,
          count: totalCount || 0,
        }
        return dataSearched
      }
    }
    case 'AZURE': {
      const { data } = await getAzureRevenueReports({
        partnerId,
        finalStartDate,
        finalEndDate,
        pageSize,
        pageNumber,
        productId,
      })
      return data
    }
    case 'GCP': {
      const { data } = await getGcpRevenueReports({
        partnerId,
        finalStartDate,
        finalEndDate,
        pageSize,
        pageNumber,
        productId,
      })
      return data
    }
  }
}

export const getRevenueInsights =
  (
    downloadReport: boolean,
    partnerId: string,
    pageSizeValue?: number,
    input?: string,
    selectedInput?: string,
    sectionName?: string,
    pageNumber?: number,
    lastEvaluatedObject?: LastEvaluatedObject | null
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const { dateRange, filters, dateRangeType, insertDates } =
        getState().revenueInsightsV2
      const cloudMarketplace = getState().productListingFilters.cloudMarketplace
      const productId =
        getState().productsListing[cloudMarketplace].selectedProduct || ''
      const finalStartDate = dateRange.startDate
      const finalEndDate = dateRange.endDate
      const { pageSize: pageSizeStateValue } = getState().revenueInsightsV2
      const pageSize = pageSizeValue ? pageSizeValue : pageSizeStateValue
      const { keys: lastEvaluatedKeys, values: lastEvaluatedValues } =
        convertObjectKeysAndValuesToCommaSeprated(lastEvaluatedObject)
      const shouldSendGetDateParam =
        insertDates && Object.keys(insertDates).length > 0 ? false : true

      if (!downloadReport) {
        const data = await handleReportFetch({
          // partnerId: '0dd05fe0-5a05-4bf8-9b4a-0f5886dd3508',
          partnerId,
          finalStartDate,
          finalEndDate,
          pageSize,
          productId,
          dateRangeType,
          lastEvaluatedKeys,
          lastEvaluatedValues,
          shouldSendGetDateParam,
          filters,
          input,
          selectedInput,
          pageNumber,
          sectionName,
          cloudMarketplace,
        })
        await dispatch(
          setReportsData(camelize(data), pageSize, cloudMarketplace)
        )
      } else {
        await downloadCSVForRevenueInsightsV2({
          partnerId,
          finalStartDate,
          finalEndDate,
          pageSize,
          productId,
          dateRangeType,
          lastEvaluatedKeys,
          lastEvaluatedValues,
          downloadReport,
          ...filters,
          input,
          selectedInput,
          pageNumber,
          sectionName,
        })
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const setReportsData = (
  data: ReportsPayload,
  pageSize: number,
  cloudMarketplace: CloudMarketplace
) => ({
  type: RevenueTrackingActionTypes.SET_REPORTS_DATA,
  payload: { ...data, pageSize, cloudMarketplace },
})
export const downloadCSVForRevenueInsightsV2 = async ({
  partnerId,
  finalStartDate,
  finalEndDate,
  pageSize,
  productId,
  dateRangeType,
  paymentStatus,
  offerType,
  disbursementDate,
  paymentDueDate,
  paymentDelayedSince,
  lastEvaluatedKeys,
  lastEvaluatedValues,
  sortBy,
  downloadReport,
  sortOrder,
  input,
  selectedInput,
  pageNumber,
  sectionName,
}: GetRevenueInsightsReportV2) => {
  //TO DO LT-2930 update default parameters

  if (isEmpty(input)) {
    return getReportsV2({
      partnerId,
      finalStartDate,
      finalEndDate,
      pageSize,
      productId,
      dateRangeType,
      paymentStatus,
      offerType,
      disbursementDate,
      paymentDueDate,
      paymentDelayedSince,
      lastEvaluatedKeys,
      lastEvaluatedValues,
      sortBy,
      downloadReport,
      sortOrder,
    }).then(response => {
      FileDownload(response.data, `${finalStartDate}_${finalEndDate}.csv`)
    })
  } else {
    return getSearchedData({
      partnerId,
      productId,
      input,
      selectedInput,
      pageSize,
      pageNumber,
      sectionName,
      isRevenueSearch: true,
      downloadToCsv: true,
    }).then(response => {
      FileDownload(response.data, `${finalStartDate}_${finalEndDate}.csv`)
    })
  }
}

export const updateRevenuePageSize =
  (pageSize: number, partnerId: string) => async (dispatch: AppDispatch) => {
    await dispatch(setRevenuePageSize(pageSize))
    await dispatch(getRevenueInsights(false, partnerId) as unknown as AnyAction)
  }

export const setRevenuePageSize = (pageSize: number) => ({
  type: RevenueTrackingActionTypes.REVENUE_SET_LIMIT,
  payload: pageSize,
})

export const setRevenuePageNumber = (pageNumber: number) => ({
  type: RevenueTrackingActionTypes.REVENUE_SET_PAGE_NUMBER,
  payload: pageNumber,
})

export const clearRevenueReports = () => ({
  type: RevenueTrackingActionTypes.CLEAR_REVENUE_REPORTS,
})

export const setPageOneForRevenue = () => async (dispatch: AppDispatch) => {
  await dispatch(setRevenuePageNumber(1))
}

export const updateDateRange =
  (dates: DateRange) => async (dispatch: AppDispatch) => {
    const { startDate, endDate } = dates
    await dispatch(setDateRange({ startDate, endDate }))
    await dispatch(clearRevenueReports())
    await dispatch(setRevenuePageNumber(1))
  }
export const setDateRange = (dates: DateRange) => ({
  type: RevenueTrackingActionTypes.SET_DATE_RANGE,
  payload: dates,
})

export const setDateRangeType = (dateRangeType: string) => ({
  type: RevenueTrackingActionTypes.SET_DATE_RANGE_TYPE,
  payload: dateRangeType,
})
export const setFilters = (filters: FiltersState) => ({
  type: RevenueTrackingActionTypes.SET_FILTERS,
  payload: filters,
})
export const clearRevenueFilters = () => ({
  type: RevenueTrackingActionTypes.CLEAR_REVENUE_FILTERS,
})
